import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RankmiTable } from '@rankmi/rankmi-table'
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import EmptyState from 'components/reusables/emptyState'
import { TableSkeleton } from 'components/reusables/skeletons'
import { ModalButtons } from 'components/reusables/modalButtons'
import { Modal } from 'components/reusables/modal'
import { ICourse } from 'interfaces/learningPath'
import { certificatesService } from 'api/administration/certificates'
import { IBasicCourse } from 'interfaces/course'
import { IProfile } from 'interfaces/profiles'
import Profiles from 'components/reusables/profiles'
import { Wrapper, RankmiTableWrapper } from './styled'

interface IProps {
  data: IBasicCourse[]
  setData: (value: IBasicCourse[]) => void
  close: () => void
}

const AddCourses = ({ data, setData, close }: IProps) => {
  const { t } = useTranslation()
  const translatePrefix = 'administration.certificate.new'
  const [courses, setCourses] = useState<IBasicCourse[]>([])
  const [selectedCourses, setSelectedCourses] = useState<IBasicCourse[]>([])
  const [loading, setLoading] = useState(false)
  const [searchCriteria, setSearchCriteria] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  useEffect(() => {
    getCourses(currentPage, '')
  }, [])

  const getCourses = async (page: number, criteria: string) => {
    setLoading(true)
    await certificatesService.getCourses(criteria, page).then((response) => {
      removeSelectedCourses(
        response.data.data.map(
          (item: { attributes: IBasicCourse }) => item.attributes
        )
      )
      setTotalRecords(response.data.meta.total)
    })
    setLoading(false)
  }

  const removeSelectedCourses = (newCourses: IBasicCourse[]) => {
    setCourses(
      newCourses
        .filter((course) => {
          let selectedCourse = false
          for (const addedCourse of data) {
            if (addedCourse.id === course.id) {
              selectedCourse = true
              break
            }
          }
          return !selectedCourse
        })
        .map((course) => {
          return {
            ...course,
            key: course.id,
          }
        })
    )
  }

  const onAdd = () => {
    setData([...data, ...selectedCourses])
    close()
  }

  const onSearch = (value: string) => {
    setSearchCriteria(value)
    setCurrentPage(1)
    getCourses(1, value)
  }

  const onPaginationChange = (value: number) => {
    setCurrentPage(value)
    getCourses(value, searchCriteria)
  }

  const onRowSelected = (value: any) => {
    if (value['dispatcher'] !== 'handleUnselectAllRowsOnFilter') {
      const courseList: IBasicCourse[] = [
        ...selectedCourses,
        ...value['selectedRows'],
      ]
      const addedCourses: number[] = []
      setSelectedCourses(
        courseList.filter((course) => {
          if (
            value['selectedRowKeys'].includes(course.id) &&
            !addedCourses.includes(course.id)
          ) {
            addedCourses.push(course.id)
            return true
          }
          return false
        })
      )
    }
  }

  const locale = {
    header: {
      rowsCount: t(`${translatePrefix}.header.rowsCount`),
      rowsCount_plural: t(`${translatePrefix}.header.rowsCountPlural`),
    },
  }

  const columns = [
    {
      title: t(`${translatePrefix}.courses.nameV2`),
      render: (data: ICourse) => data.name,
    },
    {
      title: `${t(`${translatePrefix}.courses.profiles`)}`,
      dataIndex: 'lmsProfiles',
      key: 'lmsProfiles',
      render: (lmsProfiles: IProfile[]) => <Profiles items={lmsProfiles} />,
    },
  ]

  return (
    <Modal
      title={t(`${translatePrefix}.header.addCourses`)}
      actions={
        <ModalButtons
          primaryText={t(`${translatePrefix}.buttons.save`)}
          primaryAction={onAdd}
          disablePrimaryButton={selectedCourses.length === 0}
          secondaryText={t(`${translatePrefix}.buttons.cancel`)}
          secondaryAction={close}
        />
      }
      maxheight={500}
      maxwidth={800}
      close={close}
    >
      <Wrapper>
        {loading && courses.length === 0 ? (
          <TableSkeleton />
        ) : (
          <RankmiTableWrapper>
            <RankmiTable
              locale={locale}
              columnHeaders={columns}
              data={courses}
              total={totalRecords}
              search={searchCriteria}
              onSearch={onSearch}
              onRowSelected={onRowSelected}
              onPaginationChange={onPaginationChange}
              selectedRows={selectedCourses.map((row) => row.id)}
              currentPage={currentPage}
              countIcon={faBook}
              loading={loading}
              delaySearch={true}
              customEmpty={
                <EmptyState
                  title={t(`${translatePrefix}.emptyState.title`)}
                  icon={faBook}
                />
              }
            />
          </RankmiTableWrapper>
        )}
      </Wrapper>
    </Modal>
  )
}

export default AddCourses
